import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { mockCases } from '@/mocks/cases';
import { mockChildren } from '@/mocks/children';

export default function CasesReviewPage() {
  const navigate = useNavigate();
  const [approvalStatus, setApprovalStatus] = useState<Record<string, 'approved' | 'rejected' | 'pending'>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [showNoteModal, setShowNoteModal] = useState<string | null>(null);
  const [tempNote, setTempNote] = useState('');

  const pendingCases = mockCases.filter((c) => c.supervisorApproval === 'pending');
  const allCases = mockCases;

  const handleApprove = (id: string) => setApprovalStatus((prev) => ({ ...prev, [id]: 'approved' }));
  const handleReject = (id: string) => { setShowNoteModal(id); setTempNote(''); };
  const confirmReject = (id: string) => { setApprovalStatus((prev) => ({ ...prev, [id]: 'rejected' })); setNotes((prev) => ({ ...prev, [id]: tempNote })); setShowNoteModal(null); };

  const getStatus = (caseId: string, original: string) => approvalStatus[caseId] || original;

  return (
    <AppLayout title="Cases for Review" breadcrumb={['Supervisor', 'Cases for Review']}>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-amber-700">{pendingCases.length}</p>
          <p className="text-sm text-amber-600 mt-1">Pending Review</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{Object.values(approvalStatus).filter((s) => s === 'approved').length}</p>
          <p className="text-sm text-green-600 mt-1">Approved (This Session)</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{Object.values(approvalStatus).filter((s) => s === 'rejected').length}</p>
          <p className="text-sm text-red-600 mt-1">Rejected (This Session)</p>
        </div>
      </div>

      {/* Pending Cases */}
      <div className="bg-white rounded-xl border border-amber-200 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-amber-100 bg-amber-50 flex items-center gap-2">
          <i className="ri-time-line text-amber-500" />
          <h3 className="font-semibold text-amber-800">Pending Supervisor Review</h3>
          <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">{pendingCases.length}</span>
        </div>
        {pendingCases.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <i className="ri-checkbox-circle-line text-4xl mb-2 block text-green-400" />
            <p className="text-sm">All cases have been reviewed!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {pendingCases.map((c) => {
              const child = mockChildren.find((ch) => ch.id === c.childId);
              const currentStatus = getStatus(c.id, c.supervisorApproval);
              return (
                <div key={c.id} className="px-5 py-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {child && <img src={child.photo} alt={c.childName} className="w-12 h-12 rounded-xl object-cover object-top flex-shrink-0" />}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-sm font-semibold text-gray-900">{c.childName}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.riskLevel === 'critical' ? 'bg-red-100 text-red-700' : c.riskLevel === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.riskLevel}
                          </span>
                        </div>
                        <p className="text-xs font-mono text-gray-400 mb-1">{c.caseNumber}</p>
                        <p className="text-xs text-gray-600">{c.incidentType} · {c.municipality} · Assigned to {c.assignedWorker}</p>
                        <p className="text-xs text-gray-400 mt-1">Filed: {c.createdAt} · Updated: {c.updatedAt}</p>
                        {notes[c.id] && <p className="text-xs text-red-600 mt-1 italic">Rejection note: {notes[c.id]}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => navigate(`/child-profiles/${c.childId}`)} className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                        <i className="ri-eye-line" />View
                      </button>
                      {currentStatus === 'pending' ? (
                        <>
                          <button onClick={() => handleApprove(c.id)} className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                            <i className="ri-check-line" />Approve
                          </button>
                          <button onClick={() => handleReject(c.id)} className="text-xs bg-red-100 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                            <i className="ri-close-line" />Reject
                          </button>
                        </>
                      ) : (
                        <span className={`text-xs px-3 py-1.5 rounded-lg font-medium capitalize ${currentStatus === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {currentStatus}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* All Cases Overview */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">All Cases Overview</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left font-medium">Case</th>
                <th className="px-5 py-3 text-left font-medium">Child</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Incident</th>
                <th className="px-5 py-3 text-left font-medium">Risk</th>
                <th className="px-5 py-3 text-left font-medium">Approval</th>
                <th className="px-5 py-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {allCases.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 text-xs font-mono text-gray-500">{c.caseNumber.split('-').slice(-1)[0]}</td>
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-gray-800">{c.childName}</p>
                    <p className="text-xs text-gray-400">{c.assignedWorker}</p>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-xs text-gray-600">{c.incidentType}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${c.riskLevel === 'critical' ? 'bg-red-100 text-red-700' : c.riskLevel === 'high' ? 'bg-orange-100 text-orange-700' : c.riskLevel === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                      {c.riskLevel}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${c.supervisorApproval === 'approved' ? 'bg-green-100 text-green-700' : c.supervisorApproval === 'pending' ? 'bg-amber-100 text-amber-700' : c.supervisorApproval === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                      {c.supervisorApproval.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <button onClick={() => navigate(`/child-profiles/${c.childId}`)} className="text-xs text-teal-500 hover:text-teal-700 cursor-pointer whitespace-nowrap">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Rejection Reason</h3>
            <p className="text-sm text-gray-500 mb-4">Please provide a reason for rejecting this case submission. The social worker will be notified.</p>
            <textarea value={tempNote} onChange={(e) => setTempNote(e.target.value)} rows={4} maxLength={500} placeholder="Enter rejection reason..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none mb-4" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowNoteModal(null)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={() => confirmReject(showNoteModal)} className="text-sm bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">Reject Case</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
